import {
  HolosafeText,
  IcAdministration,
  IcArrowRight,
  IcBarMenuAlert,
  IcBarMenuConfigInstall,
  IcBarMenuFlightPlan,
  IcBarMenuGroup,
  IcBarMenuMonitoring,
  IcBarMenuOrganization,
  IcBarMenuProcess,
  IcBarMenuSearch,
  IcBarMenuUser,
  IcBarMenuUtm,
  IcBarMenuVacationChange,
  IcDroneFleet,
  IcScreenConfiguration,
  IcSensorInstallation,
  IcZoneManagement,
} from '@assets/components/cockpitSidebar';
import { ReactComponent as HologardeSafeLogo } from '@assets/hologarde-safe.svg';
import { Box, HStack, Icon, Separator, VStack } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { updateIsChangingShift } from '@redux/authent/authent.reducer';
import { hasAnyRoles, hasRole, selectIsChangingShift, selectPermissions } from '@redux/authent/authent.selectors';
import { selectNewMessageEntry } from '@redux/chat/chat.selectors';
import { selectOngoingNotificationsCount } from '@redux/data/data.selectors';
import { selectNewLogBookEntry } from '@redux/global/global.selector';
import { RootState } from '@redux/store';
import { useNavigate } from 'react-router';

import { RoleEnum } from '@/types/authent/roles.types';

import { CockpitPanelIntlKeysEnum, LOGBOOK_ROLES } from '../cockpit.types';
import { useCockpitUiStateContext } from '../CockpitContexts';
import { PANEL_ROUTES } from '../CockpitRoutes';
import CockpitSidebarButton from './CockpitSidebarButton';

function CockpitSeparator() {
  return <Separator marginLeft="18px" alignSelf="start" width="40px" flexShrink={0} borderColor="neutral.700" />;
}

export default function CockpitSidebar() {
  const permissions = useAppSelector(selectPermissions);
  const navigate = useNavigate();
  const { isSidebarMaximized, setIsSidebarMaximized } = useCockpitUiStateContext();
  const notificationsCount = useAppSelector(selectOngoingNotificationsCount);
  const newMessageEntry = useAppSelector(selectNewMessageEntry);
  const newLog = useAppSelector(selectNewLogBookEntry);
  const isChangingShift = useAppSelector(selectIsChangingShift);
  const updateIsChangingShiftWithDispatch = useWithDispatch(updateIsChangingShift);
  const canSeeChat = useAppSelector((state: RootState) => hasRole(state, RoleEnum.ACCESS_MSG));
  const canSeeLogBook = useAppSelector((state) => hasAnyRoles(state, LOGBOOK_ROLES));

  return (
    <>
      {isSidebarMaximized && <Box width="74px" position="relative" height="100%" flexShrink={0} />}
      <VStack
        width={isSidebarMaximized ? '268px' : '74px'}
        height="100%"
        zIndex={10}
        gap={0}
        flexShrink={0}
        top={0}
        left={0}
        position={isSidebarMaximized ? 'absolute' : 'relative'}
        paddingY={isSidebarMaximized ? 0.25 : 0}
        paddingLeft={isSidebarMaximized ? 0.25 : 0}
        backgroundColor="neutral.black"
        alignItems="start"
        borderRight="2px solid"
        borderColor="neutral.black"
        userSelect="none"
      >
        <VStack gap={0} width="100%">
          <CockpitSidebarButton
            badge={notificationsCount !== 0}
            count={notificationsCount}
            icon={IcBarMenuAlert}
            isHidden={!permissions.NOTIFICATION}
            label={CockpitPanelIntlKeysEnum.NOTIFICATION}
          />
          <CockpitSidebarButton
            icon={IcBarMenuProcess}
            isHidden={!permissions.LOG_BOOK_AND_CHAT}
            badge={(canSeeLogBook && newLog) || (canSeeChat && newMessageEntry)}
            label={CockpitPanelIntlKeysEnum.LOG_BOOK_AND_CHAT}
          />
          <CockpitSidebarButton
            icon={IcBarMenuMonitoring}
            isHidden={!permissions.MONITORING}
            label={CockpitPanelIntlKeysEnum.MONITORING}
          />
          <CockpitSidebarButton
            icon={IcBarMenuUtm}
            label="UTM"
            isHidden={!permissions.DRONES_FLEET && !permissions.FLIGHT_PLANS}
            subMenuItems={[
              {
                icon: IcDroneFleet,
                label: CockpitPanelIntlKeysEnum.DRONES_FLEET,
                isHidden: !permissions.DRONES_FLEET,
              },
              {
                icon: IcBarMenuFlightPlan,
                label: CockpitPanelIntlKeysEnum.FLIGHT_PLANS,
                isHidden: !permissions.FLIGHT_PLANS,
              },
            ]}
          />
          <CockpitSidebarButton
            icon={IcBarMenuSearch}
            isHidden={!permissions.NOTIFICATION_SEARCH}
            label={CockpitPanelIntlKeysEnum.NOTIFICATION_SEARCH}
          />
        </VStack>
        <CockpitSeparator />
        <VStack gap={0} width="100%">
          <CockpitSidebarButton
            icon={IcBarMenuConfigInstall}
            label="CONFIGURATION"
            subMenuItems={[
              {
                icon: IcSensorInstallation,
                label: CockpitPanelIntlKeysEnum.SENSOR_INSTALLATION,
                isHidden: !permissions.SENSOR_INSTALLATION,
              },
              {
                icon: IcZoneManagement,
                label: CockpitPanelIntlKeysEnum.ZONE,
                isHidden: !permissions.ZONE,
              },
            ]}
          />
          <CockpitSidebarButton
            icon={IcAdministration}
            label="ADMINISTRATION"
            subMenuItems={[
              {
                icon: IcBarMenuOrganization,
                label: CockpitPanelIntlKeysEnum.ORGANIZATION,
                isHidden: !permissions.ORGANIZATION,
              },
              {
                icon: IcBarMenuGroup,
                label: CockpitPanelIntlKeysEnum.USERS_MANAGEMENT,
                isHidden: !permissions.USERS_MANAGEMENT,
              },
              {
                icon: IcSensorInstallation,
                label: CockpitPanelIntlKeysEnum.PLATFORM,
              },
              {
                icon: IcScreenConfiguration,
                label: CockpitPanelIntlKeysEnum.OPERATOR_STATION_CONFIGURATION,
                isHidden: !permissions.OPERATOR_STATION_CONFIGURATION,
              },
            ]}
          />
        </VStack>
        <CockpitSeparator />
        <VStack gap={0} width="100%">
          <CockpitSidebarButton
            isForcedActive={isChangingShift}
            icon={IcBarMenuVacationChange}
            label="CHANGE_SHIFT"
            onClick={() => updateIsChangingShiftWithDispatch(true)}
          />
          <CockpitSidebarButton icon={IcBarMenuUser} label={CockpitPanelIntlKeysEnum.SETTINGS} />
        </VStack>
        <VStack
          gap={0}
          width="100%"
          marginTop="auto"
          alignItems="start"
          justifyContent="end"
          flexShrink={0}
          paddingBottom="11px"
        >
          <HStack
            gap={4}
            padding={3}
            height="72px"
            cursor="pointer"
            flexShrink={0}
            onClick={() => navigate(PANEL_ROUTES[CockpitPanelIntlKeysEnum.SYSTEM_PARAMETERS])}
            className="group"
          >
            <Icon as={HologardeSafeLogo} boxSize="24px" />
            {isSidebarMaximized && (
              <Icon
                as={HolosafeText}
                color="neutral.200"
                _groupHover={{ color: 'neutral.white' }}
                height="13px"
                width="112px"
              />
            )}
          </HStack>
          <Icon
            as={IcArrowRight}
            color="neutral.300"
            alignSelf="start"
            transform={isSidebarMaximized ? 'rotate(180deg)' : undefined}
            boxSize="24px"
            marginX={3}
            _hover={{
              color: 'skyBlue',
            }}
            cursor="pointer"
            onClick={() => setIsSidebarMaximized(!isSidebarMaximized)}
          />
        </VStack>
      </VStack>
    </>
  );
}
